// -----------------------------------------------------------------------------
// Library
// -----------------------------------------------------------------------------
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { Form, Switch, Tooltip } from "antd";
// -----------------------------------------------------------------------------
// Store
// -----------------------------------------------------------------------------
import { selectSelectedWorkout } from "store/selectors";
import {
	useGetCategoriesQuery,
	useGetDomainGymQuery,
	useGetGymEquipmentQuery,
	useAddGymEquipmentMutation,
} from "store/api/api";

// -----------------------------------------------------------------------------
// Components
// -----------------------------------------------------------------------------
import { Button } from "components/Common/Button";
import { FormTagSelect } from "components/Common/Form/FormTagSelect";
import { FormSelect } from "components/Common/Form/FormSelect";
import Toggle from "components/UI/Form/Toggle";
import DateTimeTimezone from "components/DateTimeTimezone";
// -----------------------------------------------------------------------------
// Utils and Styles
// -----------------------------------------------------------------------------
import StyledWorkoutDetailsForm from "./styles";
import { defaultTimezone, durationOptions, workoutTypes, workoutStatuses } from "helpers/constants";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { sortByProperty, validEquipments } from "utils";
dayjs.extend(utc);
dayjs.extend(timezone);
// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------
const WorkoutDetailsForm = ({ nextStep, live, previousStep, values, loading, preRecordedLive }) => {
	const [form] = Form.useForm();
	const selectedWorkout = useSelector(selectSelectedWorkout);
	const { gym } = useParams();
	const domainGymResponse = useGetDomainGymQuery(gym);
	const domainGymId = domainGymResponse.data?.id;
	const isPartnerGym = !!domainGymResponse.data?.is_partner_gym;

	const [scheduleRelease, setScheduleRelease] = useState(!!values.is_scheduled);
	const [createEquipmentTrigger, addGymEquipment] = useAddGymEquipmentMutation();
	const [active, setActive] = useState(values?.status === workoutStatuses.ACTIVE);
	const [isFree, setIsFree] = useState(!!values.is_free);
	const [isPreviouslyLive, setIsPreviouslyLive] = useState(!!values.is_previously_live);
	const [currentDatetime, setCurrentDatetime] = useState(null);

	const { data: equipments, refetch } = useGetGymEquipmentQuery(domainGymId, {
		skip: !domainGymId,
	});
	const { data: categoriesData } = useGetCategoriesQuery(domainGymId, {
		skip: !domainGymId,
	});

	const handleChangeScheduling = (checked, event) => {
		setScheduleRelease(checked);
		if (checked && active) {
			setActive(false);
		}
		if (checked) {
			form.setFieldsValue({
				workoutStatus: workoutStatuses.INACTIVE,
			});
		}
	};

	const convertStartTime = (workoutDate, workoutTime) => {
		if (!workoutDate || !workoutTime) return null;
		const date = dayjs(workoutDate).format("YYYY-MM-DD");
		const startTime = dayjs.tz(`${date} ${workoutTime}`, "UTC").format("YYYY-MM-DD HH:mm:ss");

		return startTime;
	};

	const getPayload = async () => {
		const {
			workoutCategories,
			workoutEquipment,
			workoutLength,
			workoutStatus,
			workoutDate,
			workoutTime = values.workoutTime,
			workoutTimezone,
		} = form.getFieldsValue();

		const equipmentsList = await validEquipments({
			domainGymId,
			equipmentData: equipments,
			selectedEquipments: workoutEquipment,
			createEquipmentTrigger,
		});
		await refetch();
		const payload = {
			workout_id: selectedWorkout?.id,
			categories: (workoutCategories ? workoutCategories?.join(",") : "")?.trim(),
			equipment_list: equipmentsList,
			is_free: isFree?.free && isPartnerGym ? 1 : 0,
			is_scheduled: scheduleRelease ? 1 : 0,
			is_previously_live: isPreviouslyLive ? 1 : 0,
			type: scheduleRelease ? workoutTypes.SCHEDULED_ON_DEMAND : workoutTypes.ON_DEMAND,
			...(live
				? { duration: workoutLength ? workoutLength : null }
				: { status: active ? workoutStatuses.ACTIVE : workoutStatuses.INACTIVE }),
			...((!live || preRecordedLive) && {
				time_zone: scheduleRelease ? workoutTimezone : null,
				workoutDate,
				workoutTime,
				start_time: convertStartTime(workoutDate, workoutTime) || values.start_time || null,
			}),
			...(preRecordedLive && {
				type: undefined,
			}),
			status: workoutStatus || workoutStatuses.INACTIVE,
		};
		return payload;
	};

	const onFinish = async () => {
		const payload = await getPayload();
		nextStep(payload);
	};

	const onPreviousStep = async () => {
		const payload = await getPayload();
		previousStep(payload);
	};

	const workoutLength = values.duration ? values.duration : null;

	const memoizedCategoriesOptions = React.useMemo(() => {
		return sortByProperty(categoriesData?.categories || [], "name")?.map((cat) => ({
			value: cat.name,
			id: cat.id,
			label: cat.name,
			key: cat.id,
		}));
	}, [categoriesData]);

	const memoizedEquipmentOptions = React.useMemo(() => {
		return sortByProperty(equipments?.equipments || [], "name")
			?.filter(({ status }) => status === "active")
			.map((cat) => ({
				label: cat.name,
				id: cat.id,
				value: cat.id,
				disabled: cat.status !== "active",
				key: cat.id,
			}));
	}, [equipments]);

	return (
		<StyledWorkoutDetailsForm>
			<div className="sb-workout-details-form">
				<Form
					data-id="workout-details-form"
					onFinish={onFinish}
					layout="vertical"
					form={form}
					initialValues={{
						workoutCategories: values?.categories
							? values.categories?.trim()?.split(",")?.filter(Boolean) ?? undefined
							: undefined,
						workoutLength,
						workoutEquipment: values.equipment_list,
						workoutTimezone: values.time_zone || defaultTimezone,
						workoutDate: values.workoutDate,
						workoutTime: values.workoutTime,
						workoutStatus: workoutStatuses.INACTIVE,
					}}
				>
					{categoriesData?.categories?.length > 0 && (
						<FormTagSelect
							name="workoutCategories"
							label="Workout Categories"
							placeholder="Enter Categories"
							rules={[
								() => ({
									validator(_, value) {
										if (value && Array.isArray(value)) {
											const longTags = value.filter((tag) => tag.length >= 31);
											if (longTags.length > 0) {
												return Promise.reject(new Error(`Maximum allowed characters is 30 per category.`));
											}
										}
										return Promise.resolve();
									},
								}),
							]}
							style={{ minHeight: 48 }}
							options={memoizedCategoriesOptions}
						/>
					)}
					{live && !preRecordedLive && (
						<FormSelect
							name="workoutLength"
							placeholder="Workout Length"
							rules={[{ required: true, message: "This field is required" }]}
							label="Workout Length"
							options={durationOptions}
							showSearch={false}
							sizes="small"
							data-id="workoutLength"
						/>
					)}
					<div className="sb-workout-details-form__form__field">
						<FormTagSelect
							name="workoutEquipment"
							label="Workout Equipment"
							placeholder="Enter Equipment"
							rules={[
								() => ({
									validator(_, value) {
										if (value && Array.isArray(value)) {
											const longTags = value.filter((tag) => tag.length >= 256);
											if (longTags.length > 0) {
												return Promise.reject(new Error(`Maximum allowed characters is 255 per equipment.`));
											}
										}
										return Promise.resolve();
									},
								}),
								() => ({
									validator(_, value) {
										if (value && Array.isArray(value)) {
											const inactiveEquipments = equipments?.equipments
												.filter((equipment) => equipment.status !== "active")
												.map((equipment) => equipment.name.toLowerCase());
											const invalidTags = value.filter((tag) => inactiveEquipments.includes(tag.toLowerCase()));
											if (invalidTags.length > 0) {
												return Promise.reject(
													new Error(`Select inactive equipment is not allowed. (${invalidTags.join(", ")})`)
												);
											}
										}
										return Promise.resolve();
									},
								}),
							]}
							style={{ minHeight: 48 }}
							mode="tags"
							options={memoizedEquipmentOptions}
						/>
					</div>
					{!live && (
						<div style={{ width: "100%" }} className={`workout-active-toggle ${selectedWorkout.status}`}>
							<FormSelect
								label="Status"
								name="workoutStatus"
								placeholder="Select Status"
								style={{
									width: "100%",
								}}
								rules={[
									{
										required: true,
									},
								]}
								options={[
									{ label: "Draft", value: workoutStatuses.DRAFT, disabled: scheduleRelease },
									{ label: "Inactive", value: workoutStatuses.INACTIVE },
									{ label: "Active", value: workoutStatuses.ACTIVE, disabled: scheduleRelease },
								]}
							/>
						</div>
					)}
					{!live && (
						<div className="sb-workout-details-form__form__activation-schedule">
							<div>
								<span className="sb-workout-details-form__form__field__label__main">Schedule For Later?</span>
								<Tooltip title="Your workout will be activated for your members at the selected time">
									<Switch
										data-id="workoutLaterSchedule"
										checked={scheduleRelease}
										onChange={(checked) => handleChangeScheduling(checked)}
									/>
								</Tooltip>
							</div>
							{isPartnerGym && (
								<div>
									<span className="sb-workout-details-form__form__field__label__main">Mark Previously Live?</span>
									<Tooltip title="Your workout will be marked as previously live for your members">
										<Switch
											data-id="workoutPreviouslyLive"
											checked={isPreviouslyLive}
											onChange={(checked) => setIsPreviouslyLive(checked)}
										/>
									</Tooltip>
								</div>
							)}
						</div>
					)}
					{scheduleRelease && (
						<div style={{ paddingTop: "8px" }}>
							<div className="sb-form-date-timezone">
								<DateTimeTimezone
									form={form}
									currentDatetime={currentDatetime}
									setCurrentDatetime={setCurrentDatetime}
								/>
							</div>
						</div>
					)}
					{isPartnerGym && (
						<div className="sb-workout-details-form__form__field">
							<Toggle
								initial={!!values.is_free}
								name="free"
								text="Free"
								updateToggles={setIsFree}
								tooltipTitle="This is only for use by API Partners."
								data-id="workoutIsFree"
							/>
						</div>
					)}
					<div className="sb-workout-details-form__form__actions">
						<Button onClick={onPreviousStep} type="button" className="cancel-btn" data-id="btnBack">
							Previous
						</Button>
						<Button
							type="submit"
							loading={loading || addGymEquipment.isLoading}
							disabled={loading}
							className="primary-filled"
							data-id="btnSubmit"
						>
							Next
						</Button>
					</div>
				</Form>
			</div>
		</StyledWorkoutDetailsForm>
	);
};

export default WorkoutDetailsForm;

